import { Component } from '@theme/component';

/**
 * Stock Social Proof Component
 * 
 * Displays live stock scarcity and social proof indicators with:
 * - Random initial stock (15-20) per user
 * - Time-based decrementation (configurable 3-7 min intervals)
 * - Inverse sold count updates
 * - 24-hour localStorage persistence
 * - Smooth animations and progress bar
 */
class StockSocialProof extends Component {
  /**
   * Component initialization
   */
  connectedCallback() {
    super.connectedCallback();
    
    this.productId = this.dataset.productId;
    this.storageKey = `pdp_stock_state_${this.productId}`;
    this.intervalMin = parseInt(this.dataset.intervalMin) || 3;
    this.intervalMax = parseInt(this.dataset.intervalMax) || 7;
    this.showProgress = this.dataset.showProgress === 'true';
    
    // Initialize state from localStorage or generate new
    this.initializeState();
    
    // Render initial state
    this.renderState();
    
    // Start automatic decrement timer
    this.startDecrementTimer();
  }

  /**
   * Initialize or restore state from localStorage
   */
  initializeState() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      
      if (stored) {
        const data = JSON.parse(stored);
        
        // Check if state is still valid (not expired)
        if (data.expiresAt && Date.now() < data.expiresAt) {
          this.state = data;
          return;
        }
      }
    } catch (error) {
      console.warn('[stock-social-proof] Failed to restore state:', error);
    }
    
    // Generate new random state if no valid stored state
    this.generateNewState();
  }

  /**
   * Generate fresh random state
   */
  generateNewState() {
    // Random stock between 15-20
    const initialStock = Math.floor(Math.random() * 6) + 15;
    
    // Random sold count between 100-150
    const soldCount = Math.floor(Math.random() * 51) + 100;
    
    this.state = {
      initialStock: initialStock,
      currentStock: initialStock,
      soldCount: soldCount,
      expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
      lastDecrement: Date.now()
    };
    
    this.saveState();
  }

  /**
   * Render current state to DOM
   */
  renderState() {
    if (!this.refs.stockNumber || !this.refs.soldNumber) {
      console.warn('[stock-social-proof] Required refs not found');
      return;
    }

    // Update stock number
    this.refs.stockNumber.textContent = this.state.currentStock;
    
    // Update sold count
    this.refs.soldNumber.textContent = this.state.soldCount;
    
    // Update progress bar if visible
    if (this.showProgress && this.refs.progressFill && this.refs.progressTrack) {
      const percentage = (this.state.currentStock / this.state.initialStock) * 100;
      // Minimum 6% width for visual consistency
      const displayPercentage = Math.max(percentage, 6);
      this.refs.progressFill.style.width = `${displayPercentage}%`;
      this.refs.progressTrack.setAttribute('aria-valuenow', Math.round(percentage));
    }
    
    // Handle "final piece" state
    if (this.state.currentStock === 1 && this.refs.stockText) {
      this.refs.stockText.innerHTML = 
        'Final piece — Only <strong class="stock-number stock-number--final" ref="stockNumber">1</strong> left in stock';
      // Re-update refs after innerHTML change
      this.refs.stockNumber = this.refs.stockText.querySelector('.stock-number');
    }
  }

  /**
   * Start the automatic decrement timer with random intervals
   */
  startDecrementTimer() {
    const scheduleNext = () => {
      // Convert seconds to milliseconds
      const minMs = this.intervalMin * 1000;
      const maxMs = this.intervalMax * 1000;
      
      // Random interval between min and max
      const randomInterval = Math.floor(Math.random() * (maxMs - minMs)) + minMs;
      
      this.decrementTimer = setTimeout(() => {
        this.decrementStock();
        
        // Schedule next decrement if stock > 1
        if (this.state.currentStock > 1) {
          scheduleNext();
        }
      }, randomInterval);
    };
    
    scheduleNext();
  }

  /**
   * Decrement stock by 1 and increment sold count
   */
  decrementStock() {
    // Don't decrement below 1 (maintain urgency)
    if (this.state.currentStock <= 1) {
      return;
    }
    
    // Update state
    this.state.currentStock--;
    this.state.soldCount++;
    this.state.lastDecrement = Date.now();
    
    // Persist to localStorage
    this.saveState();
    
    // Trigger animations
    this.animateUpdate();
    
    // Update DOM
    this.renderState();
  }

  /**
   * Trigger slide-up animations on number changes
   */
  animateUpdate() {
    if (!this.refs.stockNumber || !this.refs.soldNumber) {
      return;
    }

    // Add animation class
    this.refs.stockNumber.classList.add('updating');
    this.refs.soldNumber.classList.add('updating');
    
    // Remove after animation completes
    setTimeout(() => {
      if (this.refs.stockNumber && this.refs.soldNumber) {
        this.refs.stockNumber.classList.remove('updating');
        this.refs.soldNumber.classList.remove('updating');
      }
    }, 600);
  }

  /**
   * Save state to localStorage with error handling
   */
  saveState() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (error) {
      // Handle localStorage errors (quota exceeded, private browsing, etc.)
      console.warn('[stock-social-proof] Failed to save state:', error);
    }
  }

  /**
   * Cleanup when component is removed from DOM
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Clear timer to prevent memory leaks
    if (this.decrementTimer) {
      clearTimeout(this.decrementTimer);
      this.decrementTimer = null;
    }
  }
}

// Register custom element
if (!customElements.get('stock-social-proof')) {
  customElements.define('stock-social-proof', StockSocialProof);
}
